import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Unauthorized from "./pages/Unauthorized";
import AsyncInterview from "./pages/AsyncInterview";
import ProspectOnboarding from "./pages/ProspectOnboarding";
import RecruiterDashboard from "./pages/RecruiterDashboard";
import Sourcing from "./pages/Sourcing";
import ProspectDetail from "./pages/ProspectDetail";
import MyCandidates from "./pages/MyCandidates";
import CandidateDetail from "./pages/CandidateDetail";
import Positions from "./pages/Positions";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCompanies from "./pages/admin/AdminCompanies";
import AddCompany from "./pages/admin/AddCompany";
import AdminCompanyDetail from "./pages/admin/AdminCompanyDetail";
import AdminCompanyEdit from "./pages/admin/AdminCompanyEdit";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import ProspectRegistration from "./pages/ProspectRegistration";
import ProspectDashboard from "./pages/ProspectDashboard";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import MyJobs from "./pages/MyJobs";
import ProspectProfile from "./pages/ProspectProfile";
import PublicJobsList from "./pages/PublicJobsList";
import MyCompany from "./pages/MyCompany";
import CompanyAnalytics from "./pages/CompanyAnalytics";
import RecruiterTasks from "./pages/RecruiterTasks";
import RecruiterDocuments from "./pages/RecruiterDocuments";
import RecruiterJobBoards from "./pages/RecruiterJobBoards";
import RecruiterPipelines from "./pages/RecruiterPipelines";
import RecruiterAgencies from "./pages/RecruiterAgencies";
import AgentLogin from "./pages/agent/AgentLogin";
import AgentAccept from "./pages/agent/AgentAccept";
import AgentDashboard from "./pages/agent/AgentDashboard";
import AddPosition from "./pages/AddPosition";
import EditPosition from "./pages/EditPosition";
import PositionDetail from "./pages/PositionDetail";
import CompanyManagement from "./pages/CompanyManagement";
import Settings from "./pages/Settings";
import JobiChatbox from "./components/JobiChatbox";
import PublicJobBoard from "./pages/PublicJobBoard";
import PublicJobDetail from "./pages/PublicJobDetail";
import PublicApply from "./pages/PublicApply";
import PublicCompanyProfile from "./pages/PublicCompanyProfile";
import PublicLayout from "./components/PublicLayout";
import VideoCall from "./pages/VideoCall";
import JobseekerLanding from "./pages/JobseekerLanding";
import CompanyLanding from "./pages/CompanyLanding";
import CompanyDemo from "./pages/CompanyDemo";
import Onboarding from "./pages/Onboarding";
import SalesDashboard from "./pages/SalesDashboard";
import Accounts from "./pages/Accounts";
import AccountDetail from "./pages/AccountDetail";
import SalesTasks from "./pages/SalesTasks";
import SalesContacts from "./pages/SalesContacts";
import SalesActivities from "./pages/SalesActivities";
import SalesJobs from "./pages/SalesJobs";
import SalesJobDetail from "./pages/SalesJobDetail";
import SalesCandidates from "./pages/SalesCandidates";
import SalesPlacements from "./pages/SalesPlacements";
import SalesCommission from "./pages/SalesCommission";
import SalesPipeline from "./pages/SalesPipeline";
import SalesCompanies from "./pages/SalesCompanies";
import SalesReports from "./pages/SalesReports";
import SalesSettings from "./pages/SalesSettings";
import LanguageWelcomeBanner from "./components/LanguageWelcomeBanner";
import PublicJobTranslateBanner from "./components/PublicJobTranslateBanner";
import { Toaster } from "sonner";

// Legacy invite link redirector: /register?token=… → /onboarding?token=…
const RegisterRedirector = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (token) return <Navigate to={`/onboarding?token=${token}`} replace />;
  // No token — recruiter self-signup is disabled (invite-only)
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          {/* Global toast notifications (used by Jobi action feedback etc.) */}
          <Toaster position="top-right" richColors closeButton />
          {/* Jobi Chatbox - appears for admins and recruiters */}
          <JobiChatbox />
          {/* Smart language suggestion (first visit, browser-lang mismatch) */}
          <LanguageWelcomeBanner />
          {/* Google Translate suggestion on public job posting pages */}
          <PublicJobTranslateBanner />
          <Routes>
            {/* Marketing / landing — public, unauthenticated */}
            <Route path="/" element={<CompanyLanding />} />
            <Route path="/for-companies" element={<Navigate to="/" replace />} />
            <Route path="/demo" element={<CompanyDemo />} />
            <Route path="/for-companies/demo" element={<Navigate to="/demo" replace />} />
            <Route path="/for-jobseekers" element={<JobseekerLanding />} />

            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            {/* External agent portal (independent, Supabase-auth) */}
            <Route path="/agent/login" element={<AgentLogin />} />
            <Route path="/agent/accept" element={<AgentAccept />} />
            <Route path="/agent" element={<AgentDashboard />} />
            <Route path="/onboarding" element={<Onboarding />} />
            {/* Legacy: /register (with ?token=) → /onboarding; without token → /login */}
            <Route path="/register" element={<RegisterRedirector />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/prospect-register" element={<ProspectRegistration />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Async interview public landing (token-based) */}
            <Route path="/async-interview/:token" element={<AsyncInterview />} />

            {/* Public job board routes (no auth required) */}
            <Route path="/:slug/jobs" element={<PublicLayout><PublicJobBoard /></PublicLayout>} />
            <Route path="/:slug/jobs/:id" element={<PublicLayout><PublicJobDetail /></PublicLayout>} />
            <Route path="/:slug/jobs/:id/apply" element={<PublicLayout><PublicApply /></PublicLayout>} />
            <Route path="/:slug/about" element={<PublicLayout><PublicCompanyProfile /></PublicLayout>} />

            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />

            {/* Sourcing — Jobasti Admin only */}
            <Route 
              path="/sourcing" 
              element={
                <ProtectedRoute roles={['JADM']}>
                  <Sourcing />
                </ProtectedRoute>
              } 
            />

            {/* JobastiIQ Sales CRM — Sales Executives (+ Super Admin oversight) */}
            <Route
              path="/sales-dashboard"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <Accounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/:id"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <AccountDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-tasks"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesTasks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm-contacts"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesContacts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm-activities"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesActivities />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm-jobs"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesJobs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm-jobs/:id"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesJobDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm-candidates"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesCandidates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm-placements"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesPlacements />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm-commission"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesCommission />
                </ProtectedRoute>
              }
            />
            <Route
              path="/software-pipeline"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesPipeline />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-companies"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesCompanies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-reports"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-settings"
              element={
                <ProtectedRoute roles={['SEXE', 'JADM']}>
                  <SalesSettings />
                </ProtectedRoute>
              }
            />

            {/* Prospect Detail — Jobasti Admin only (sourcing pool browsing) */}
            <Route 
              path="/sourcing/:id" 
              element={
                <ProtectedRoute roles={['JADM']}>
                  <ProspectDetail />
                </ProtectedRoute>
              } 
            />

            {/* Prospect Detail - Alternative route */}
            <Route 
              path="/prospects/:id" 
              element={
                <ProtectedRoute roles={['JADM', 'RADM', 'RECR']}>
                  <ProspectDetail />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/candidates" 
              element={
                <ProtectedRoute roles={['JADM', 'RADM', 'RECR']}>
                  <MyCandidates />
                </ProtectedRoute>
              } 
            />

            {/* Candidate Detail - View application details */}
            <Route 
              path="/candidates/:applicationId" 
              element={
                <ProtectedRoute roles={['JADM', 'RADM', 'RECR']}>
                  <CandidateDetail />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/positions" 
              element={
                <ProtectedRoute roles={['JADM', 'RADM', 'RECR']}>
                  <Positions />
                </ProtectedRoute>
              } 
            />

            {/* Company Management - Client Admin only */}
            <Route 
              path="/company" 
              element={
                <ProtectedRoute roles={['RADM', 'client_admin', 'CADM']}>
                  <CompanyManagement />
                </ProtectedRoute>
              } 
            />

            {/* User Profile + Settings - For recruiters and admins (one component, two routes) */}
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute roles={['JADM', 'RADM', 'RECR', 'CADM', 'CREC', 'SEXE', 'jobasti_admin', 'client_admin', 'client_recruiter', 'sales_executive']}>
                  <Settings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/user-profile" 
              element={
                <ProtectedRoute roles={['JADM', 'RADM', 'RECR', 'CADM', 'CREC', 'SEXE', 'jobasti_admin', 'client_admin', 'client_recruiter', 'sales_executive']}>
                  <Settings />
                </ProtectedRoute>
              } 
            />

            {/* Prospect onboarding */}
            <Route 
              path="/onboarding" 
              element={
                <ProtectedRoute roles={['PROS']}>
                  <ProspectOnboarding />
                </ProtectedRoute>
              } 
            />

            {/* Prospect Dashboard */}
            <Route 
              path="/prospect-dashboard" 
              element={
                <ProtectedRoute roles={['PROS']}>
                  <ProspectDashboard />
                </ProtectedRoute>
              } 
            />

            {/* Public Indeed-style all-companies job board */}
            <Route path="/jobs" element={<PublicJobsList />} />

            {/* Prospect-only personalised matches (still protected) */}
            <Route
              path="/matches"
              element={
                <ProtectedRoute roles={['PROS']}>
                  <Jobs />
                </ProtectedRoute>
              }
            />

            <Route 
              path="/jobs/:id" 
              element={
                <ProtectedRoute roles={['PROS']}>
                  <JobDetail />
                </ProtectedRoute>
              } 
            />

            {/* My Jobs - Prospect's applied jobs */}
            <Route 
              path="/my-jobs" 
              element={
                <ProtectedRoute roles={['PROS']}>
                  <MyJobs />
                </ProtectedRoute>
              } 
            />

            {/* My Profile - Prospect's CV + personal details */}
            <Route
              path="/my-profile"
              element={
                <ProtectedRoute roles={['PROS']}>
                  <ProspectProfile />
                </ProtectedRoute>
              }
            />

            {/* My Company page for client admins and recruiters */}
            <Route 
              path="/my-company" 
              element={
                <ProtectedRoute roles={['CADM', 'CREC']}>
                  <MyCompany />
                </ProtectedRoute>
              } 
            />
            {/* Company analytics — now a dedicated nav page (moved out of the Company tabs) */}
            <Route 
              path="/company-analytics" 
              element={
                <ProtectedRoute roles={['CADM', 'CREC']}>
                  <CompanyAnalytics />
                </ProtectedRoute>
              } 
            />

            {/* Recruiter Tasks — full-page task manager (synced with dashboard To-Do) */}
            <Route 
              path="/tasks" 
              element={
                <ProtectedRoute roles={['CADM', 'CREC']}>
                  <RecruiterTasks />
                </ProtectedRoute>
              } 
            />

            {/* Recruiter Documents — shared company file library */}
            <Route 
              path="/documents" 
              element={
                <ProtectedRoute roles={['CADM', 'CREC']}>
                  <RecruiterDocuments />
                </ProtectedRoute>
              } 
            />

            {/* Recruiter Job Boards — browse-only directory */}
            <Route 
              path="/job-boards" 
              element={
                <ProtectedRoute roles={['CADM', 'CREC']}>
                  <RecruiterJobBoards />
                </ProtectedRoute>
              } 
            />

            {/* Recruiter Pipelines — role-centric candidates / boards / interviews / activity */}
            <Route 
              path="/pipelines" 
              element={
                <ProtectedRoute roles={['CADM', 'CREC']}>
                  <RecruiterPipelines />
                </ProtectedRoute>
              } 
            />

            {/* Recruiter Agencies — external sourcing agents + submissions */}
            <Route 
              path="/agencies" 
              element={
                <ProtectedRoute roles={['CADM', 'CREC']}>
                  <RecruiterAgencies />
                </ProtectedRoute>
              } 
            />

            {/* Video Call page */}
            <Route 
              path="/video-call" 
              element={
                <ProtectedRoute roles={['JADM', 'CADM', 'CREC', 'PROS']}>
                  <VideoCall />
                </ProtectedRoute>
              } 
            />

            {/* Add Position for recruiters */}
            <Route 
              path="/add-position" 
              element={
                <ProtectedRoute roles={['JADM', 'RADM', 'RECR']}>
                  <AddPosition />
                </ProtectedRoute>
              } 
            />

            {/* View Position Detail for recruiters */}
            <Route 
              path="/positions/:id" 
              element={
                <ProtectedRoute roles={['JADM', 'RADM', 'RECR']}>
                  <PositionDetail />
                </ProtectedRoute>
              } 
            />

            {/* Edit Position for recruiters */}
            <Route 
              path="/positions/:id/edit" 
              element={
                <ProtectedRoute roles={['JADM', 'RADM', 'RECR']}>
                  <EditPosition />
                </ProtectedRoute>
              } 
            />

            {/* Admin routes - Jobasti Admin only */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute roles={['JADM']}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute roles={['JADM']}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/companies" 
              element={
                <ProtectedRoute roles={['JADM']}>
                  <AdminCompanies />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/companies/add" 
              element={
                <ProtectedRoute roles={['JADM']}>
                  <AddCompany />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/companies/:companyId" 
              element={
                <ProtectedRoute roles={['JADM']}>
                  <AdminCompanyDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/companies/:companyId/edit" 
              element={
                <ProtectedRoute roles={['JADM']}>
                  <AdminCompanyEdit />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/users" 
              element={
                <ProtectedRoute roles={['JADM']}>
                  <AdminUsers />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/settings" 
              element={
                <ProtectedRoute roles={['JADM']}>
                  <AdminSettings />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/admin/analytics" 
              element={
                <ProtectedRoute roles={['JADM']}>
                  <AdminAnalytics />
                </ProtectedRoute>
              } 
            />

            {/* Default redirect — `/` is handled by JobseekerLanding above */}
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;